/**
 * SettingsRail — the Console's persistent left navigation.
 *
 * A second-level nav INSIDE the content area (distinct from the global app
 * sidebar), listing the seven settings areas. Active item gets a brand-purple
 * left border + card fill, mirroring the approved Concept-A mockup. The rail
 * leads with the settings-search entry point (`/` or ⌘K opens the palette).
 *
 * On narrow viewports the rail collapses to a horizontal scroll strip above
 * the panel; on lg+ the PAGE keeps it sticky so it never scrolls away.
 */
import { useEffect } from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SETTINGS_AREAS, type SettingsArea, type SettingsAreaId } from '../../areas';

interface SettingsRailProps {
  activeArea: SettingsAreaId;
  onSelect: (area: SettingsAreaId) => void;
  /** Opens the ⌘K settings-search palette (the rail is its visible home). */
  onOpenSearch: () => void;
  /** Hide admin-only sections from counts for non-admins. */
  isAdmin: boolean;
}

function visibleSectionCount(area: SettingsArea, isAdmin: boolean): number {
  return area.sections.filter((s) => isAdmin || !s.adminOnly).length;
}

export function SettingsRail({ activeArea, onSelect, onOpenSearch, isAdmin }: SettingsRailProps) {
  // `/` opens the settings-search palette (unless typing in a field already).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      e.preventDefault();
      onOpenSearch();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpenSearch]);

  return (
    <nav aria-label="Settings areas" className="flex flex-col gap-2 lg:w-60 lg:shrink-0">
      <button
        type="button"
        onClick={onOpenSearch}
        className="flex h-8 items-center gap-2 rounded-md border bg-background px-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
      >
        <Search className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-left">Search settings</span>
        <kbd
          aria-hidden
          className="rounded border bg-muted px-1 font-mono text-[10px] text-muted-foreground"
        >
          ⌘K
        </kbd>
      </button>

      <div
        role="list"
        className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:pb-0"
      >
        {SETTINGS_AREAS.map((area) => {
          const Icon = area.icon;
          const active = area.id === activeArea;
          const count = visibleSectionCount(area, isAdmin);
          return (
            <button
              key={area.id}
              role="listitem"
              type="button"
              onClick={() => onSelect(area.id)}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'group flex shrink-0 items-center gap-2.5 rounded-md border-l-2 border-transparent px-2.5 py-2 text-left text-sm transition-colors',
                'lg:w-full lg:shrink',
                active
                  ? 'border-l-threadheads-purple bg-card text-foreground shadow-sm ring-1 ring-border/60'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
              )}
            >
              <Icon
                className={cn('h-4 w-4 shrink-0', active ? 'text-primary' : 'text-muted-foreground')}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 truncate font-medium">{area.title}</span>
              <span
                className={cn(
                  'hidden rounded-full px-1.5 font-mono text-[10px] tabular-nums lg:inline',
                  active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <p className="mt-auto hidden border-t pt-3 text-[11px] leading-relaxed text-muted-foreground lg:block">
        7 areas · badges show where each setting is saved
      </p>
    </nav>
  );
}
